import { getLocaleText } from "shared/boot/i18n";
import { type AlertType } from "shared/components/base/AlertMessage.vue";
import { type SocialConnectionsType } from "shared/components/integrations/SocialConnectionOption.vue";
import { type PaginationData } from "shared/components/Pagination";
import type InstagramHandlesOrganisationSocialIntegration from "shared/resources/InstagramHandlesOrganisationSocialIntegration";
import type InstagramHashtagsOrganisationSocialIntegration from "shared/resources/InstagramHashtagsOrganisationSocialIntegration";
import ApiClient from "shared/services/api/ApiClient";
import {
  type ConvertKeysToSnakeCase,
  type Maybe,
  type NonNullableKey,
} from "shared/types";

const locale = (key: string) =>
  getLocaleText(`manage_social_connections.${key}`);

export type ConnectionId = NonNullable<
  InstagramHandlesOrganisationSocialIntegration["id"]
>;

export type IntegrationWithId = NonNullableKey<
  InstagramHandlesOrganisationSocialIntegration,
  "id"
>;

export type Alert = {
  type: AlertType;
  message: string;
};

export type HashtagId = NonNullable<
  InstagramHashtagsOrganisationSocialIntegration["id"]
>;

export type HashtagWithId = NonNullableKey<
  InstagramHashtagsOrganisationSocialIntegration,
  "id"
>;

export type ManageSocialConnectionsState = {
  account: string;
  accountCount: number;
  accountCountTotal: number;
  hashtag: string;
  hashtagCollection: Map<HashtagId, HashtagWithId>;
  hashtagTotalCollection: Map<HashtagId, HashtagWithId>;
  instagramHandleSocialConnections: Map<ConnectionId, IntegrationWithId>;
  loading: boolean;
  organisationSocialIntegrationId: Maybe<string>;
  pagination: PaginationData;
  searchQuery: string;
  selectedConnection: SocialConnectionsType;
  selectedHashtagIds: Set<HashtagId>;
  selectedIntegrationIds: Set<ConnectionId>;
};
export type AccountErrors = {
  integrationExists: boolean;
  usernameNotFound: boolean;
};

export type AccountApiErrors = ConvertKeysToSnakeCase<AccountErrors>;

export const ACCOUNT_ERRORS = {
  integration_exists: locale("account_added_error_duplicate"),
  username_not_found: locale("account_not_found"),
  default: locale("account_added_error"),
} as const;

export type HashtagErrors = {
  hashtagNotFound: boolean;
  integrationExists: boolean;
  limitReached: boolean;
};

export type HashtagApiErrors = ConvertKeysToSnakeCase<HashtagErrors>;

export const HASHTAG_ERRORS = {
  integration_exists: locale("error_duplicate_hashtag"),
  hashtag_not_found: locale("hashtag_added_error_invalid"),
  limit_reached: locale("hashtag_added_error_limit_reached"),
  default: locale("hashtag_added_error"),
} as const;

export type AccountErrorKey = keyof typeof ACCOUNT_ERRORS;

export const getAccountError = (error: unknown): string => {
  if (!ApiClient.isApiError<AccountApiErrors>(error)) {
    return ACCOUNT_ERRORS.default;
  }

  const errorData = error?.response?.data;

  switch (true) {
    case Boolean(errorData?.integration_exists):
      return ACCOUNT_ERRORS.integration_exists;
    case Boolean(errorData?.username_not_found):
      return ACCOUNT_ERRORS.username_not_found;
    default:
      return ACCOUNT_ERRORS.default;
  }
};

export type HashtagErrorKey = keyof typeof HASHTAG_ERRORS;

export const getHashtagError = (error: unknown): string => {
  if (!ApiClient.isApiError<HashtagApiErrors>(error)) {
    return HASHTAG_ERRORS.default;
  }

  const errorData = error?.response?.data;

  switch (true) {
    case Boolean(errorData?.hashtag_not_found):
      return HASHTAG_ERRORS.hashtag_not_found;
    case Boolean(errorData?.integration_exists):
      return HASHTAG_ERRORS.integration_exists;
    case Boolean(errorData?.limit_reached):
      return HASHTAG_ERRORS.limit_reached;
    default:
      return HASHTAG_ERRORS.default;
  }
};
